import React from 'react';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import TableChartIcon from '@mui/icons-material/TableChartOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';

const MODEL_TYPE_LABELS = {
  DATABASE: 'Base de datos',
  API: 'API',
  FILE: 'Archivo',
  CONNECTION: 'Conexión'
};

const SemanticModelCard = ({
  record,
  databaseName,
  onView,
  onEdit,
  onDelete,
  onGraphView,
  onSelect,
  isSelected = false,
  isDeleted = false
}) => {
  const tablesCount = Array.isArray(record?.contained_tables_id)
    ? record.contained_tables_id.length
    : 0;
  const usersCount = Array.isArray(record?.users_allowed) ? record.users_allowed.length : 0;
  const isPublic = !!record?.is_public;
  const isActive = record?.is_active !== false && !record?.soft_delete?.deleted;
  const deletedAt = record?.soft_delete?.deleted_at;
  const statusLabel = isDeleted ? 'Eliminado' : isActive ? 'Activo' : 'Inactivo';
  const statusColor = isDeleted ? 'var(--wb-danger)' : isActive ? 'var(--wb-success-strong)' : 'var(--wb-warning)';

  const modelType =
    record?.model_type ||
    record?.metadata?.model_type ||
    record?.METADATA?.model_type ||
    record?.METADATA?.MODEL_TYPE ||
    'Sin tipo';
  const modelTypeLabel = MODEL_TYPE_LABELS[modelType] || modelType;

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: isSelected ? 'var(--wb-x-bg-6366f11a, rgba(99, 102, 241, 0.1))' : 'var(--wb-bg-card)',
        borderRadius: 2,
        p: 2,
        mb: 2,
        border: isSelected ? '1px solid var(--wb-indigo)' : '1px solid var(--wb-overlay-10)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: isSelected ? 'var(--wb-x-bg-6366f126, rgba(99, 102, 241, 0.15))' : 'var(--wb-x-bg-ffffff05, rgba(255, 255, 255, 0.02))',
          borderColor: isSelected ? 'var(--wb-indigo)' : 'var(--wb-x-border-ffffff33, rgba(255, 255, 255, 0.2))',
        }
      }}
      onClick={() => onSelect && onSelect(record)}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              bgcolor: 'var(--wb-x-bg-3b82f61f, rgba(59, 130, 246, 0.12))',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <InsightsIcon sx={{ color: 'var(--wb-blue)' }} />
          </Box>
        </Grid>
        <Grid item xs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" color="var(--wb-text-strong)" sx={{ fontWeight: 600 }}>
              {record?.name || 'Modelo sin nombre'}
            </Typography>
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: 'var(--wb-overlay-08)',
                color: statusColor,
                borderRadius: 1
              }}
            />
            {record?.version_id && (
              <Chip
                label={`Versión ${record.version_id}`}
                size="small"
                sx={{ backgroundColor: 'var(--wb-x-bg-94a3b82e, rgba(148, 163, 184, 0.18))', color: 'var(--wb-text-label)' }}
              />
            )}
          </Box>

          <Typography variant="body2" color="var(--wb-text-soft)" sx={{ mt: 0.5 }}>
            {record?.description || 'Sin descripción registrada.'}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip
                label={modelTypeLabel}
                size="small"
                sx={{ backgroundColor: 'var(--wb-x-bg-2563eb2e, rgba(37, 99, 235, 0.18))', color: 'var(--wb-x-text-bfdbfe, #bfdbfe)' }}
              />
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TableChartIcon sx={{ color: 'var(--wb-muted)', fontSize: 18 }} />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {tablesCount === 1 ? '1 tabla incluida' : `${tablesCount} tablas incluidas`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <GroupIcon sx={{ color: 'var(--wb-muted)', fontSize: 18 }} />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {usersCount === 1 ? '1 usuario autorizado' : `${usersCount} usuarios autorizados`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PublicIcon
                sx={{ color: isPublic ? 'var(--wb-success)' : 'var(--wb-muted)', fontSize: 18 }}
              />
              <Typography variant="body2" color="var(--wb-text-soft)">
                {isPublic ? 'Público' : 'Privado'}
              </Typography>
            </Stack>
          </Stack>

          <Typography variant="caption" color="var(--wb-text-faint)" sx={{ display: 'block', mt: 1 }}>
            {databaseName ? `Base semántica: ${databaseName}` : 'Base semántica no asignada'}
          </Typography>

          {isDeleted && deletedAt && (
            <Typography variant="caption" color="var(--wb-x-text-f87171d9, rgba(248, 113, 113, 0.85))" sx={{ display: 'block', mt: 1 }}>
              Eliminado el {new Date(deletedAt).toLocaleString()}
            </Typography>
          )}
        </Grid>
        <Grid item>
          {/* Los clics en las acciones no deben llegar al onClick de la
              tarjeta: su setSearchParams (seleccionar el modelo) navegaba a
              /modelos?modelId=... y pisaba la navegación del ícono de grafo. */}
          <Box sx={{ display: 'flex', gap: 1 }} onClick={(event) => event.stopPropagation()}>
            <Tooltip title="Ver detalles">
              <IconButton onClick={() => onView(record)} sx={{ color: 'var(--wb-text-strong)' }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista de grafo" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onGraphView && onGraphView(record)}
                  sx={{ color: isDeleted ? 'var(--wb-text-ghost)' : 'var(--wb-blue)' }}
                  disabled={isDeleted}
                >
                  <AccountTreeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Editar" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onEdit(record)}
                  sx={{ color: 'var(--wb-text-strong)' }}
                  disabled={isDeleted}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Eliminar" disableHoverListener={isDeleted}>
              <span>
                <IconButton
                  onClick={() => onDelete(record)}
                  sx={{ color: isDeleted ? 'var(--wb-text-ghost)' : 'var(--wb-danger)' }}
                  disabled={isDeleted}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SemanticModelCard;
